import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { userPermissionListSelectors } from '../../selectors';
import { combineLatestWith, map, of, switchMap } from 'rxjs';
import { flatten, uniqBy } from 'lodash';
import * as i0 from "@angular/core";
import * as i1 from "@ngrx/store";
export class UserPermissionsService {
    constructor(store) {
        this.store = store;
        if (!UserPermissionsService.userPermListSubscription) {
            UserPermissionsService.userPermListSubscription = this.store
                .pipe(select(userPermissionListSelectors.getUserPermissions))
                .subscribe((userPerms) => (UserPermissionsService.userPermissions = userPerms));
        }
        if (!UserPermissionsService.userAppAdminListSubscription) {
            UserPermissionsService.userAppAdminListSubscription = this.store
                .pipe(select(userPermissionListSelectors.getUserAdminApplications))
                .subscribe((userApps) => (UserPermissionsService.userAdminApplications = userApps));
        }
        if (!UserPermissionsService.userAppListSubscription) {
            UserPermissionsService.userAppListSubscription = this.store
                .pipe(select(userPermissionListSelectors.getUserApplications))
                .subscribe((userApps) => (UserPermissionsService.userApplications = userApps));
        }
        if (!UserPermissionsService.activeAppSubscription) {
            UserPermissionsService.activeAppSubscription = this.store
                .pipe(select(userPermissionListSelectors.getActiveApplication))
                .subscribe((activeApp) => (UserPermissionsService.activeApplication = activeApp));
        }
    }
    hasFeature(featureKey) {
        if (UserPermissionsService.activeApplication) {
            return (this.canAccessAdminApp(UserPermissionsService.activeApplication.id) ||
                !!UserPermissionsService.userPermissions.find((perm) => perm.feature.key === featureKey));
        }
        return !!UserPermissionsService.userPermissions.find((perm) => perm.feature.key === featureKey);
    }
    hasFeatureAsync(featureKey) {
        return this.store.pipe(select(userPermissionListSelectors.getUserPermissions), combineLatestWith(this.store.pipe(select(userPermissionListSelectors.getActiveApplication))), switchMap(([permissions, activeApp]) => {
            if (activeApp) {
                return this.canAccessAdminAppAsync(activeApp.id).pipe(map((accessApp) => (accessApp ||
                    !!permissions.find((permission) => permission.feature.key === featureKey))));
            }
            return of(!!permissions.find((permission) => permission.feature.key === featureKey));
        }));
    }
    /**
     * Gets a condensed (no duplicate) list of permissions, with metadata concatenated together
     *
     * @returns {Observable<Permission[]>}
     * @memberof UserPermissionsService
     */
    condensedUserPermissions() {
        return this.store.pipe(select(userPermissionListSelectors.getUserPermissions), map((permissions) => {
            const condensedPermissions = [];
            permissions.forEach((perm) => {
                const existingPermission = condensedPermissions.find((cPerm) => cPerm.feature.id === perm.feature.id);
                if (existingPermission) {
                    if (existingPermission.metadata && perm.metadata) {
                        existingPermission.metadata = [...existingPermission.metadata, ...perm.metadata];
                    }
                }
                else {
                    condensedPermissions.push(perm);
                }
            });
            return condensedPermissions;
        }));
    }
    permissionsToMetadataList(permissions, removeDuplicates = true) {
        if (!permissions || permissions.length === 0) {
            return [];
        }
        const filteredPermissions = permissions.filter((permission) => permission.metadata && permission.metadata.length > 0) ||
            [];
        const flatArray = flatten(filteredPermissions.map((permission) => permission.metadata)).filter((metadata) => !!metadata);
        if (removeDuplicates) {
            return uniqBy(flatArray, 'name');
        }
        else {
            return flatArray;
        }
    }
    getFeatureMetadataList(featureKey, removeDuplicates = true) {
        const permissionsForFeature = UserPermissionsService.userPermissions.filter((perm) => perm.feature.key === featureKey);
        return this.permissionsToMetadataList(permissionsForFeature, removeDuplicates);
    }
    getFeatureMetadataListAsync(featureKey, removeDuplicates = true) {
        return this.store.pipe(select(userPermissionListSelectors.getUserPermissions), switchMap((permissions) => {
            const permissionsForFeature = permissions.filter((perm) => perm.feature.key === featureKey);
            return of(this.permissionsToMetadataList(permissionsForFeature, removeDuplicates));
        }));
    }
    canAccessApp(appId) {
        return !!UserPermissionsService.userApplications.find((app) => app.id === appId);
    }
    canAccessAppAsync(appId) {
        return this.store.pipe(select(userPermissionListSelectors.getUserApplications), switchMap((apps) => of(!!apps.find((app) => app.id === appId))));
    }
    canAccessAdminApp(appId) {
        return !!UserPermissionsService.userAdminApplications.find((app) => app.id === appId);
    }
    canAccessAdminAppAsync(appId) {
        return this.store.pipe(select(userPermissionListSelectors.getUserAdminApplications), switchMap((apps) => of(!!apps.find((app) => app.id === appId))));
    }
    static { this.ɵfac = function UserPermissionsService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || UserPermissionsService)(i0.ɵɵinject(i1.Store)); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: UserPermissionsService, factory: UserPermissionsService.ɵfac }); }
}
//# sourceMappingURL=user-permissions.service.js.map